/**
 * 017-style structure copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/roles-permissions/users-table/index.tsx — the
 * same filterable/paged admin list frame `../customers/List.tsx` and `../orders/List.tsx` compose
 * (`PagedTable`, `ListHeader`, the toolbar's search/status/filters slots). Non-ecommerce, same
 * reason as `customers/List.tsx`: this is a record list, not a product list, and `users-table` is
 * the template's own person/record table.
 *
 * Only the data, columns and labels are ours. `GET /api/identity/audit` (`AuditController.cs`,
 * US8) is genuinely server-paged (`skip`/`take`, capped at 200 — SC-016), so this is `PagedTable`,
 * not the in-memory `ReportTable` `invoicing/Audit.tsx` uses for one invoice's whole (small) trail.
 *
 * Gated by `identity.audit.read` in `lib/nav.tsx`, like every other route; the services' own
 * `[Authorize]` refusal (403) is still the authority and reaches this route's `errorElement`.
 *
 * `?source=` switches from Identity's trail (the default) to one service's own audit log (§19:
 * prices and tiers, credit and tier assignment, order approvals, packing entries and overrides,
 * invoices and payments, Tally sync). Each service keeps its own log; nothing is merged here.
 */
import { useLoaderData, useSearchParams, type LoaderFunctionArgs } from "react-router-dom";
import { EmptyState } from "@steelyard/theme";
import { PagedTable } from "../../components/table/data-table";
import { ListHeader } from "../../components/table/list-header";
import { auditColumns, serviceAuditColumns } from "./columns";
import { Filters } from "./filters";
import { AUDIT_SOURCES, isAuditSource, listAuditLog, listServiceAudit } from "../../lib/services/audit";
import { hrefWith, readPaging } from "../../lib/paging";

export async function auditListLoader({ request }: LoaderFunctionArgs) {
  const params = new URL(request.url).searchParams;
  const { page, pageSize } = readPaging(params);
  const source = params.get("source");

  if (isAuditSource(source)) {
    const service = await listServiceAudit(source, page, pageSize);
    return { kind: "service" as const, source, service };
  }

  const subjectAccountId = params.get("subjectAccountId") ?? undefined;
  const eventType = params.get("eventType") ?? undefined;
  const from = params.get("from") ?? undefined;
  const to = params.get("to") ?? undefined;

  const result = await listAuditLog({ page, pageSize, subjectAccountId, eventType, from, to });

  // The filter chip names its subject from the rows' own server-sent name — the id never shows.
  const subjectName = subjectAccountId
    ? (result.items.find((row) => row.subjectAccountId === subjectAccountId)?.subjectName ?? null)
    : null;

  return { kind: "identity" as const, result, subjectAccountId, eventType, from, to, subjectName };
}

export default function AuditList() {
  const data = useLoaderData() as Awaited<ReturnType<typeof auditListLoader>>;
  const [searchParams] = useSearchParams();

  const sourceKey = data.kind === "service" ? data.source : "identity";
  // A different source is a different endpoint with its own filter shape (identity's
  // subjectAccountId/eventType/from/to mean nothing to a service's own audit log), so those drop —
  // but pageSize is a display preference, not a filter, and every other list here preserves it.
  const sources = {
    label: "Log",
    options: [{ key: "identity", label: "Sign-in & users" }, ...AUDIT_SOURCES].map((option) => ({
      label: option.label,
      href: hrefWith("/audit", searchParams, {
        source: option.key === "identity" ? undefined : option.key,
        subjectAccountId: undefined,
        eventType: undefined,
        from: undefined,
        to: undefined,
        page: undefined,
      }),
      active: option.key === sourceKey,
    })),
  };
  const header = (count: number, description: string) => (
    <ListHeader
      title="Audit Log"
      count={count}
      description={`${description} Append-only — nothing here can be edited or removed.`}
      breadcrumb={[{ name: "Home", href: "/" }, { name: "Audit Log" }]}
    />
  );

  if (data.kind === "service") {
    const label = AUDIT_SOURCES.find((option) => option.key === data.source)!.label;
    return (
      <div className="flex flex-col gap-6">
        {header(data.service.totalCount, `${label}: every change this service has recorded, newest first.`)}
        <PagedTable
          result={data.service}
          columns={serviceAuditColumns}
          getRowId={(row) => row.id}
          caption={`${label} audit entries: when, what happened, to which record, and who`}
          status={sources}
          empty={<EmptyState title="No entries">No change has been recorded here yet.</EmptyState>}
        />
      </div>
    );
  }

  const { result, subjectAccountId, eventType, from, to, subjectName } = data;
  const query = (over: Record<string, string | undefined>) => hrefWith("/audit", searchParams, over);
  const filtered = Boolean(subjectAccountId || eventType || from || to);

  return (
    <div className="flex flex-col gap-6">
      {header(result.totalCount, "Every authentication and administration event that Identity has recorded, newest first.")}

      <PagedTable
        result={result}
        columns={auditColumns}
        getRowId={(row) => row.id}
        caption="Audit entries: when, what happened, its outcome, who and what it was about"
        status={sources}
        filters={<Filters value={{ subjectAccountId, eventType, from, to }} subjectName={subjectName} query={query} />}
        clear={{ active: filtered, href: "/audit" }}
        empty={
          <EmptyState title="No entries matched">
            {filtered
              ? "Nothing matched those filters. Clear them to see the whole trail."
              : "No event has been recorded yet."}
          </EmptyState>
        }
      />
    </div>
  );
}
